import { routes } from '@sunucupark/shared/lite';
import type { Branding } from '@sunucupark/shared';
import type { ApiClient } from '@sunucupark/client-core';
import { contractSchema } from '@/shared/contracts';

/** How long the browser may keep a brand before asking again (the gateway says the same). */
const BRAND_TIMEOUT_MS = 4_000;

/** The design-token custom property a colour role writes to (ADR 0009 §6). */
function cssVariable(role: string): string {
  return `--ds-${role.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

/**
 * Paints the brand: the roles are written as CSS custom properties with
 * CSSOM, never as an injected `<style>` element, so the strict CSP needs no
 * `unsafe-inline` (ADR 0007 §8). Dark values are applied when the page is in
 * the dark scheme; switching the theme re-applies them.
 *
 * The greys go on with the accents. A customer who chose blue should not be
 * reading their mail on our warm sand: the gateway tints the neutrals with
 * their hue and checks the text on them (ADR 0009 §5), and a brand that does
 * not carry them — an older gateway — simply leaves ours in place.
 */
export function applyBrandRoles(brand: Branding, scheme: 'light' | 'dark'): void {
  const roles: Record<string, string> = {
    ...brand.roles[scheme],
    ...(brand.roles.neutrals?.[scheme] ?? {}),
  };
  const style = document.documentElement.style;
  for (const [role, value] of Object.entries(roles)) {
    style.setProperty(cssVariable(role), value);
  }
}

/** Puts the brand's name in the tab and its favicon in the head. */
export function applyBrandChrome(brand: Branding): void {
  document.title = brand.name;
  const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (icon) icon.href = brand.assets.favicon;
}

/**
 * The brand the gateway serves. It decides the product name, the artwork and
 * the colours, so nothing user-visible is hard-coded (ADR 0009, ADR 0010).
 *
 * @param host the address this page was opened at, which is the only thing
 *   known before anyone signs in
 * @param domain the signed-in address's own domain, which wins over the host:
 *   a customer on our shared webmail sees their own brand once they are in
 */
export async function loadBranding(api: ApiClient, host: string, domain?: string): Promise<Branding> {
  return api.request({
    path: routes.branding.current,
    query: domain ? { host, domain } : { host },
    schema: contractSchema((contracts) => contracts.BrandingSchema),
    auth: 'none',
    timeoutMs: BRAND_TIMEOUT_MS,
  });
}
